import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { UserCheck, UserX, Clock, ShieldCheck, Users, Shield, ShieldOff, MessageCircle, Archive, RotateCcw, Trash2, Database, CheckSquare, Square, Bug, Palette, KeyRound, Wrench, ScanSearch, BarChart3, ShieldQuestion, Wallet, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import BrandingTab from '../components/BrandingTab';
import NotificationSettingsPanel from '../components/NotificationSettingsPanel';

// iter244 — everyday vs advanced tool split so the panel isn't a wall of tiles
const EVERYDAY_TOOLS = [
  { icon: MessageCircle, color: 'text-green-500', hover: 'hover:border-green-500/50', title: 'WhatsApp', desc: 'Notifications & reminders', path: '/whatsapp-settings', testId: 'whatsapp-settings-link' },
  { icon: Archive, color: 'text-amber-600', hover: 'hover:border-amber-500/50', title: 'Groups Archive', desc: 'Hide finished/test groups', path: '/admin/groups', testId: 'groups-archive-link' },
  { icon: Database, color: 'text-blue-500', hover: 'hover:border-blue-500/50', title: 'Backups', desc: 'Daily auto-backup', path: '/backups', testId: 'backups-link' },
  { icon: ShieldCheck, color: 'text-purple-500', hover: 'hover:border-purple-500/50', title: 'Audit Log', desc: 'Who did what, when', path: '/audit-log', testId: 'audit-log-link' },
  { icon: BarChart3, color: 'text-emerald-500', hover: 'hover:border-emerald-500/50', title: 'Platform Analytics', desc: 'Passkey + PWA adoption funnels', path: '/admin/analytics', testId: 'platform-analytics-link' },
];
const ADVANCED_TOOLS = [
  { icon: Wallet, color: 'text-emerald-500', hover: 'hover:border-emerald-500/50', title: 'Wallet Admin', desc: 'Credits, loans, cash reconciliation', path: '/admin/wallets', testId: 'wallet-admin-link' },
  { icon: Bug, color: 'text-red-500', hover: 'hover:border-red-500/50', title: 'Error Logs', desc: 'Crashes → copy to Emergent', path: '/error-logs', testId: 'error-logs-link' },
  { icon: KeyRound, color: 'text-cyan-500', hover: 'hover:border-cyan-500/50', title: 'NeoPay creds', desc: 'DB-stored audit-mirror keys', path: '/admin/neopay-settings', testId: 'neopay-settings-link' },
  { icon: ShieldQuestion, color: 'text-violet-500', hover: 'hover:border-violet-500/50', title: 'NeoPay Assurance', desc: 'Retry queue + chain verifier', path: '/admin/neopay-assurance', testId: 'neopay-assurance-link' },
  { icon: Wrench, color: 'text-amber-500', hover: 'hover:border-amber-500/50', title: 'Guest Cleanup', desc: 'Scrub phantom guest_xxx balances', path: '/admin/guest-cleanup', testId: 'guest-cleanup-link' },
  { icon: Wrench, color: 'text-orange-500', hover: 'hover:border-orange-500/50', title: 'Fund Repair', desc: 'Scan & fix reversal drift', path: '/admin/fund-repair', testId: 'fund-repair-link' },
  { icon: ScanSearch, color: 'text-purple-500', hover: 'hover:border-purple-500/50', title: 'Ledger Sanity', desc: 'Backfill missing payer credits', path: '/admin/ledger-sanity', testId: 'ledger-sanity-link' },
];

const ToolCard = ({ tool, navigate }) => (
  <Card
    className={`p-3 border-2 rounded-2xl cursor-pointer transition-colors ${tool.hover}`}
    onClick={() => navigate(tool.path)}
    data-testid={tool.testId}
  >
    <div className="flex items-center gap-2">
      <tool.icon className={`w-5 h-5 ${tool.color}`} />
      <div>
        <h4 className="font-semibold text-sm">{tool.title}</h4>
        <p className="text-xs text-muted-foreground">{tool.desc}</p>
      </div>
    </div>
  </Card>
);

export default function AdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pendingUsers, setPendingUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      const [pending, all] = await Promise.all([
        api.get('/auth/admin/pending-users'),
        api.get('/auth/admin/all-users'),
      ]);
      setPendingUsers(pending.data);
      setAllUsers(all.data);
      setSelectedUsers(new Set());
    } catch { toast.error('Failed to load users'); }
    finally { setLoading(false); }
  };

  const handleApprove = async (userId, userName) => {
    try {
      const res = await api.put(`/auth/admin/users/${userId}/approve`);
      toast.success(res.data?.message || `${userName} approved!`);
      fetchUsers();
    }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };
  const handleReject = async (userId, userName) => {
    try { await api.put(`/auth/admin/users/${userId}/reject`); toast.success(`${userName} rejected`); fetchUsers(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };
  const handlePromote = async (userId, userName) => {
    // iter249 — this grants PLATFORM-WIDE control; make the tap deliberate
    const ok = window.confirm(
      `Make ${userName} a SUPER ADMIN?\n\nThis gives them FULL platform control — approve/archive users, all groups, WhatsApp settings, backups and every admin tool.\n\n(For a single group, use "Make group admin" inside that group's Members tab instead.)`
    );
    if (!ok) return;
    try { await api.put(`/users/${userId}/promote-admin`); toast.success(`${userName} is now a Super Admin`); fetchUsers(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };
  const handleDemote = async (userId, userName) => {
    try { await api.put(`/users/${userId}/demote-admin`); toast.success(`${userName} demoted`); fetchUsers(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };
  const handleArchive = async (userId, userName) => {
    if (!window.confirm(`Archive ${userName}?`)) return;
    try { await api.put(`/auth/admin/users/${userId}/archive`); toast.success(`${userName} archived`); fetchUsers(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };
  const handleReactivate = async (userId, userName) => {
    try { await api.put(`/auth/admin/users/${userId}/reactivate`); toast.success(`${userName} reactivated!`); fetchUsers(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };
  const handleDelete = async (userId, userName) => {
    if (!window.confirm(`Permanently delete ${userName}?`)) return;
    try { await api.delete(`/auth/admin/users/${userId}`); toast.success(`${userName} deleted`); fetchUsers(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };

  // Select all / toggle
  const toggleSelect = (userId) => {
    const next = new Set(selectedUsers);
    if (next.has(userId)) next.delete(userId); else next.add(userId);
    setSelectedUsers(next);
  };
  const toggleSelectAll = () => {
    if (selectedUsers.size === allUsers.length) setSelectedUsers(new Set());
    else setSelectedUsers(new Set(allUsers.map(u => u.id)));
  };

  const handleBulkArchive = async () => {
    const ids = [...selectedUsers].filter(id => id !== user.id);
    if (ids.length === 0) { toast.error('No users selected (cannot archive yourself)'); return; }
    if (!window.confirm(`Archive ${ids.length} user(s)? They will no longer be able to login.`)) return;
    setBulkProcessing(true);
    try {
      const res = await api.post('/auth/admin/users/bulk-archive', { user_ids: ids });
      const msg = res.data.message;
      // Show balance warnings
      const withBalances = res.data.archived?.filter(a => a.pending_balances) || [];
      if (withBalances.length > 0) {
        const warnings = withBalances.map(a => `${a.name}: ${a.pending_balances.join(', ')}`).join('\n');
        toast.warning(`${msg}\n\nUsers with pending balances:\n${warnings}`, { duration: 8000 });
      } else {
        toast.success(msg);
      }
      fetchUsers();
    } catch (error) { toast.error(error.response?.data?.detail || 'Bulk archive failed'); }
    finally { setBulkProcessing(false); }
  };

  if (!user?.is_super_admin) {
    return (
      <div className="p-6 text-center" data-testid="admin-forbidden">
        <ShieldCheck className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
        <h2 className="font-display text-xl font-bold">Admin Only</h2>
      </div>
    );
  }

  const statusColors = {
    pending: 'bg-yellow-500/20 text-yellow-600',
    approved: 'bg-green-500/20 text-green-600',
    rejected: 'bg-red-500/20 text-red-600',
    archived: 'bg-gray-500/20 text-gray-500',
  };

  const UserCard = ({ u, showActions, selectable }) => (
    <Card className={`p-4 border-2 rounded-2xl ${selectedUsers.has(u.id) ? 'border-primary/50 bg-primary/5' : ''}`} data-testid={`user-card-${u.id}`}>
      <div className="flex items-start gap-3">
        {selectable && (
          <button onClick={() => toggleSelect(u.id)} className="mt-1 shrink-0" data-testid={`select-user-${u.id}`}>
            {selectedUsers.has(u.id)
              ? <CheckSquare className="w-5 h-5 text-primary" />
              : <Square className="w-5 h-5 text-muted-foreground" />}
          </button>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <span className="text-xs font-bold text-primary">{u.name?.charAt(0).toUpperCase()}</span>
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className="font-semibold text-sm">{u.name}</h4>
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold uppercase ${statusColors[u.status] || statusColors.pending}`}>
                  {u.status || 'pending'}
                </span>
                {u.is_super_admin && <span className="text-xs px-1.5 py-0.5 rounded-full bg-primary/20 text-primary font-semibold">Super Admin</span>}
              </div>
              <p className="text-xs text-muted-foreground">{u.phone}</p>
            </div>
          </div>
        </div>
        {showActions && (
          <div className="flex flex-col gap-1 items-end shrink-0">
            {u.status === 'pending' && (
              <div className="flex gap-1">
                <Button size="sm" onClick={() => handleApprove(u.id, u.name)} data-testid={`approve-user-${u.id}`} className="rounded-full h-7 text-xs">
                  <UserCheck className="w-3 h-3 mr-1" />Approve
                </Button>
                <Button size="sm" variant="destructive" onClick={() => handleReject(u.id, u.name)} data-testid={`reject-user-${u.id}`} className="rounded-full h-7 text-xs">
                  <UserX className="w-3 h-3 mr-1" />Reject
                </Button>
              </div>
            )}
            {u.status === 'rejected' && (
              <Button size="sm" onClick={() => handleApprove(u.id, u.name)} data-testid={`reapprove-user-${u.id}`} className="rounded-full h-7 text-xs">
                <UserCheck className="w-3 h-3 mr-1" />Approve
              </Button>
            )}
            {u.status === 'approved' && u.id !== user.id && (
              u.is_super_admin ? (
                <Button size="sm" variant="outline" onClick={() => handleDemote(u.id, u.name)} data-testid={`demote-admin-${u.id}`} className="rounded-full h-7 text-xs">
                  <ShieldOff className="w-3 h-3 mr-1" />Demote
                </Button>
              ) : (
                <Button size="sm" variant="outline" onClick={() => handlePromote(u.id, u.name)} data-testid={`promote-admin-${u.id}`} className="rounded-full h-7 text-xs border-amber-500/60 text-amber-700 dark:text-amber-400 hover:bg-amber-500/10">
                  <Shield className="w-3 h-3 mr-1" />Make Super Admin
                </Button>
              )
            )}
            {u.id !== user.id && (
              <div className="flex gap-1">
                {u.status === 'approved' && (
                  <Button size="sm" variant="ghost" onClick={() => handleArchive(u.id, u.name)} data-testid={`archive-user-${u.id}`} className="rounded-full h-6 text-xs text-gray-500">
                    <Archive className="w-3 h-3 mr-0.5" />Archive
                  </Button>
                )}
                {u.status === 'archived' && (
                  <Button size="sm" variant="ghost" onClick={() => handleReactivate(u.id, u.name)} data-testid={`reactivate-user-${u.id}`} className="rounded-full h-6 text-xs text-green-600">
                    <RotateCcw className="w-3 h-3 mr-0.5" />Reactivate
                  </Button>
                )}
                {(u.status === 'archived' || u.status === 'rejected') && (
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(u.id, u.name)} data-testid={`delete-user-${u.id}`} className="rounded-full h-6 text-xs text-red-500">
                    <Trash2 className="w-3 h-3 mr-0.5" />Delete
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );

  return (
    <div className="p-6 max-w-2xl lg:max-w-none mx-auto space-y-6" data-testid="admin-page">
      <div>
        <h1 className="font-display text-2xl font-bold">Admin</h1>
        <p className="text-muted-foreground text-sm mt-1">Users, tools & settings</p>
      </div>

      {/* Quick Links — everyday tools */}
      <div className="grid grid-cols-2 gap-3">
        {EVERYDAY_TOOLS.map((t) => <ToolCard key={t.testId} tool={t} navigate={navigate} />)}
      </div>

      {/* iter301 — app-wide notification master toggles */}
      <NotificationSettingsPanel />

      {/* iter244 — advanced/danger tools collapsed by default */}
      <button
        type="button"
        onClick={() => setShowAdvanced((v) => !v)}
        data-testid="advanced-tools-toggle"
        className="w-full py-2.5 rounded-xl border border-dashed border-border text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors flex items-center justify-center gap-1"
      >
        {showAdvanced ? 'Hide advanced tools' : `Advanced tools (${ADVANCED_TOOLS.length})`}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
      </button>
      {showAdvanced && (
        <div className="grid grid-cols-2 gap-3" data-testid="advanced-tools-grid">
          {ADVANCED_TOOLS.map((t) => <ToolCard key={t.testId} tool={t} navigate={navigate} />)}
        </div>
      )}

      {pendingUsers.length > 0 && (
        <Card className="p-3 border-2 rounded-2xl bg-yellow-500/5 border-yellow-500/30" data-testid="pending-alert">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-yellow-600" />
            <p className="font-semibold text-sm">{pendingUsers.length} pending approval{pendingUsers.length > 1 ? 's' : ''}</p>
          </div>
        </Card>
      )}

      <Tabs defaultValue="pending">
        <TabsList className="grid grid-cols-3 h-12 rounded-xl">
          <TabsTrigger value="pending" className="rounded-lg" data-testid="pending-users-tab">
            <Clock className="w-4 h-4 mr-2" /> Pending ({pendingUsers.length})
          </TabsTrigger>
          <TabsTrigger value="all" className="rounded-lg" data-testid="all-users-tab">
            <Users className="w-4 h-4 mr-2" /> All ({allUsers.length})
          </TabsTrigger>
          <TabsTrigger value="branding" className="rounded-lg" data-testid="branding-tab">
            <Palette className="w-4 h-4 mr-2" /> Branding
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="space-y-3 mt-4">
          {loading ? (
            <p className="text-muted-foreground text-center py-8">Loading...</p>
          ) : pendingUsers.length === 0 ? (
            <div className="text-center py-12" data-testid="no-pending">
              <UserCheck className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No pending signups</p>
            </div>
          ) : (
            pendingUsers.map((u) => <UserCard key={u.id} u={u} showActions={true} selectable={false} />)
          )}
        </TabsContent>

        <TabsContent value="all" className="space-y-3 mt-4">
          {/* Select All + Bulk Actions Bar */}
          <div className="flex items-center justify-between px-1">
            <button onClick={toggleSelectAll} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="select-all-users">
              {selectedUsers.size === allUsers.length && allUsers.length > 0
                ? <CheckSquare className="w-4 h-4 text-primary" />
                : <Square className="w-4 h-4" />}
              {selectedUsers.size > 0 ? `${selectedUsers.size} selected` : 'Select all'}
            </button>
            {selectedUsers.size > 0 && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleBulkArchive}
                disabled={bulkProcessing}
                data-testid="bulk-archive-btn"
                className="rounded-full text-xs"
              >
                <Archive className="w-3 h-3 mr-1" />
                {bulkProcessing ? 'Archiving...' : `Archive ${selectedUsers.size}`}
              </Button>
            )}
          </div>
          {allUsers.map((u) => <UserCard key={u.id} u={u} showActions={true} selectable={true} />)}
        </TabsContent>

        <TabsContent value="branding" className="mt-4">
          <BrandingTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
